import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { SignIn, UserPlus, Eye, EyeSlash } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = isRegister
      ? await register(email, password, name)
      : await login(email, password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/holotrak-logo.png"
            alt="Holotrak"
            className="h-10 object-contain"
            data-testid="holotrak-logo"
          />
        </div>

        <Card className="bg-zinc-900 border-zinc-800 rounded-sm p-6" data-testid="login-card">
          <h2 className="text-lg font-semibold text-zinc-50 mb-1 text-center">
            {isRegister ? "Create Account" : "Sign In"}
          </h2>
          <p className="text-xs text-zinc-500 text-center mb-6">
            {isRegister
              ? "Register with your invited email"
              : "Cold Chain Analytics Platform"}
          </p>

          {error && (
            <div
              className="bg-red-500/10 border border-red-900/50 text-red-400 text-xs p-3 rounded-sm mb-4"
              data-testid="auth-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label className="text-xs text-zinc-400">Full Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className="mt-1 bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-teal-400"
                  data-testid="register-name"
                />
              </div>
            )}
            <div>
              <Label className="text-xs text-zinc-400">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="mt-1 bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-teal-400"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  minLength={6}
                  className="bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-teal-400 pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-500 hover:bg-teal-600 text-zinc-950 font-semibold"
              data-testid="login-submit-btn"
            >
              {isRegister ? (
                <><UserPlus size={16} className="mr-2" />{loading ? "Creating..." : "Create Account"}</>
              ) : (
                <><SignIn size={16} className="mr-2" />{loading ? "Signing in..." : "Sign In"}</>
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => { setIsRegister(!isRegister); setError(""); }}
              className="text-xs text-teal-400 hover:text-teal-300 transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? "Already have an account? Sign In" : "Have an invite? Create Account"}
            </button>
          </div>
        </Card>

        <p className="text-[10px] text-zinc-700 text-center mt-6">
          Holotrak Cold Chain Analytics
        </p>
      </div>
    </div>
  );
}
